import { FormattedMessage } from 'react-intl';

import type { FilterWidget } from '@sorare/core/src/lib/filters';
import { FILTERS } from '@sorare/core/src/lib/filters';
import { cardAttributes, glossary } from '@sorare/core/src/lib/glossary';

import { widget as ShowDetails } from '#src/components/TokenPreview/ShowDetails/index.tsx';
import { BackendDuplicateFilter } from '#src/searchCards/DuplicateFilter/index.tsx';
import { RefineBackendCardPlayerAge } from '#src/searchCards/RefineAge/index.tsx';
import { RefineBackendAppearances } from '#src/searchCards/RefineAppearances/index.tsx';
import { RefineBackendAverageScore } from '#src/searchCards/RefineAverageScore/index.tsx';
import {
  RefineActiveClub,
  RefineBackendActiveClub,
  RefineBackendClub,
  RefineClub,
} from '#src/searchCards/RefineClubs/index.tsx';
import { RefineBackendGameplayTier } from '#src/searchCards/RefineGameplayTier/index.tsx';
import {
  RefineBackendActiveLeague,
  RefineBackendActiveNationalTeam,
  RefineBackendCardEdition,
  RefineBackendNationality,
  RefineBackendPlayer,
  RefineBackendPlayingStatus,
  RefineBackendSeason,
} from '#src/searchCards/RefineList/index.tsx';
import { backendWidget as BackendRefineSkin } from '#src/searchCards/RefineSkin/index.tsx';
import {
  AcceptedCurrencyGroup,
  BackendAcceptedCurrencyGroup,
  BackendBidToEarnFilter,
  BackendCollectibleFilter,
  BackendGameweekFilter,
  BackendNotInLineupFilter,
  BackendPriceGroup,
  BackendPromotionalEventFilter,
  BackendRarityGroup,
  BackendRefineCompetitionEligibility,
  BackendRefineCreditEligible,
  BackendRefineInSeason,
  BackendRefineOnSale,
  BackendSaleGroup,
  BackendWatchlistGroup,
  BidToEarnFilter,
  CollectibleFilter,
  CollectibleGroup,
  GameweekFilter,
  NotInLineupFilter,
  PriceGroup,
  PromotionalEventFilter,
  PromotionalEventFilterForInstantBuy,
  RarityGroup,
  RefineActiveLeague,
  RefineActiveNationalTeam,
  RefineAppearances,
  RefineAverageScore,
  RefineBackendCardLevel,
  RefineBackendPosition,
  RefineBackendSerialNumber,
  RefineBackendStartingPrediction,
  RefineCardEdition,
  RefineCardLevel,
  RefineCardPlayerAge,
  RefineCompetitionEligibility,
  RefineCreditEligible,
  RefineInSeason,
  RefineNationality,
  RefineOnSale,
  RefinePlayer,
  RefinePlayingStatus,
  RefinePosition,
  RefineSeason,
  BackendRevealableCardCampaignMenu,
  RefineSerialNumber,
  RefineStartingPrediction,
  SaleGroup,
  filterSeparator,
} from '#src/searchCards/index.ts';
import { WatchlistGroup } from '#src/searchCards/refineGroups.tsx';

import { backendWidget as BackendFootballCustomDecksFilter } from './FootballCustomDecksFilter/BackendFootballCustomDecksFilter.tsx';
import { widget as FootballCustomDecksFilter } from './FootballCustomDecksFilter/index.tsx';

const CardGroup = (props?: {
  withoutTeam?: boolean;
  withoutCompetitionEligibility: boolean;
}) => ({
  slug: 'card',
  title: <FormattedMessage {...glossary.card} />,
  filters: [
    ...((props?.withoutCompetitionEligibility && []) || [
      RefineCompetitionEligibility,
    ]),
    RefinePosition(),
    RefineSeason,
    ...((props?.withoutTeam && []) || [RefineClub()]),
  ].filter(Boolean),
});

export const MatchInfoGroup = ({
  withStartingPrediction = false,
}: { withStartingPrediction?: boolean } = {}) => ({
  slug: 'match-info',
  title: <FormattedMessage {...glossary.matchInfo} />,
  filters: [
    GameweekFilter,
    RefineAverageScore(),
    RefineAppearances(),
    ...(withStartingPrediction ? [RefineStartingPrediction] : []),
  ],
});

export const PlayerGroup = (props?: {
  withoutActiveClub?: boolean;
  withoutNationalTeam?: boolean;
  withPositions?: boolean;
  withPlayingStatus?: boolean;
}) => ({
  slug: 'player',
  title: <FormattedMessage {...cardAttributes.player} />,
  filters: [
    ...(props?.withPositions ? [RefinePosition()] : []),
    ...(props?.withPlayingStatus ? [RefinePlayingStatus] : []),
    RefineCardPlayerAge,
    ...((props?.withoutNationalTeam && []) || [RefineNationality]),
    RefinePlayer,
    RefineActiveLeague(),
    ...((props?.withoutActiveClub && []) || [RefineActiveClub()]),
    ...((props?.withoutNationalTeam && []) || [RefineActiveNationalTeam]),
  ].filter(Boolean),
});

const ToggleGroup = (additionalFilters?: FilterWidget[]) => ({
  slug: 'toggle',
  title: null,
  filters: [
    ...(additionalFilters?.flatMap(filter => [filterSeparator, filter]) || []),
  ],
});
const InSeasonFilter = {
  slug: 'inSeason',
  title: null,
  filter: RefineInSeason,
  filters: [],
};

export const primaryMarketFilters = [
  {
    slug: 'PromotionalEvent',
    title: null,
    filters: [PromotionalEventFilter],
  },
  RarityGroup,
  PriceGroup,
  ToggleGroup([BidToEarnFilter()]),
  WatchlistGroup,
  CardGroup(),
  MatchInfoGroup({ withStartingPrediction: true }),
  PlayerGroup(),
  CollectibleGroup([RefineSerialNumber, RefineCardEdition]),
  SaleGroup,
];

export const instantBuyMarketFilters = [
  {
    slug: 'PromotionalEvent',
    title: null,
    filters: [PromotionalEventFilterForInstantBuy],
  },
  RarityGroup,
  PriceGroup,
  WatchlistGroup,
  CardGroup(),
  MatchInfoGroup({ withStartingPrediction: true }),
  PlayerGroup(),
  CollectibleGroup([RefineSerialNumber, RefineCardEdition]),
];

export const secondaryMarketFilters = [
  InSeasonFilter,
  RarityGroup,
  PriceGroup,
  WatchlistGroup,
  CardGroup(),
  MatchInfoGroup({ withStartingPrediction: true }),
  PlayerGroup(),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter,
  ]),
  AcceptedCurrencyGroup,
];

export const secondaryMarketStackedFilters = [
  InSeasonFilter,
  PriceGroup,
  CardGroup(),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter,
  ]),
  AcceptedCurrencyGroup,
].filter(Boolean);

export const playerPageFilters = [
  InSeasonFilter,
  ToggleGroup([RefineOnSale(), RefineCreditEligible()]),
  RarityGroup,
  PriceGroup,
  CardGroup({ withoutCompetitionEligibility: true }),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter,
  ]),
  AcceptedCurrencyGroup,
];

export const tradeFilters = [
  InSeasonFilter,
  RarityGroup,
  { slug: 'not-in-lineup', filters: [NotInLineupFilter] },
  WatchlistGroup,
  {
    slug: 'deck',
    filters: [FootballCustomDecksFilter],
  },
  CardGroup({ withoutCompetitionEligibility: false, withoutTeam: false }),
];

export const clubPageFilters = [
  InSeasonFilter,
  ToggleGroup([RefineOnSale(), RefineCreditEligible()]),
  RarityGroup,
  PriceGroup,
  WatchlistGroup,
  CardGroup({ withoutCompetitionEligibility: true, withoutTeam: true }),
  PlayerGroup(),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter,
  ]),
  AcceptedCurrencyGroup,
];

export const leaguePageFilters = [
  InSeasonFilter,
  ToggleGroup([RefineOnSale(), RefineCreditEligible()]),
  RarityGroup,
  PriceGroup,
  WatchlistGroup,
  CardGroup({ withoutCompetitionEligibility: true }),
  MatchInfoGroup(),
  PlayerGroup(),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter,
  ]),
  AcceptedCurrencyGroup,
];

export const countryPageFilters = [
  InSeasonFilter,
  ToggleGroup([RefineOnSale(), RefineCreditEligible()]),
  RarityGroup,
  PriceGroup,
  WatchlistGroup,
  CardGroup(),
  MatchInfoGroup(),
  PlayerGroup({ withoutNationalTeam: true }),
  CollectibleGroup([
    RefineSerialNumber,
    RefineCardLevel,
    RefineCardEdition,
    CollectibleFilter,
  ]),
  AcceptedCurrencyGroup,
];

const BackendCardGroup = (props?: {
  withoutTeam?: boolean;
  withoutCompetitionEligibility?: boolean;
}) => ({
  slug: 'card',
  title: <FormattedMessage {...glossary.card} />,
  filters: [
    ...((props?.withoutCompetitionEligibility && []) || [
      BackendRefineCompetitionEligibility,
    ]),
    RefineBackendPosition(),
    RefineBackendSeason,
    ...((props?.withoutTeam && []) || [RefineBackendClub()]),
  ].filter(Boolean),
});

// The starting prediction is only written to the cards-on-sale index, so a surface reading the full
// card index would filter on whatever the last full reindex left behind.
const BackendMatchInfoGroup = ({
  withStartingPrediction = false,
}: { withStartingPrediction?: boolean } = {}) => ({
  slug: 'match-info',
  title: <FormattedMessage {...glossary.matchInfo} />,
  filters: [
    BackendGameweekFilter,
    RefineBackendAverageScore(),
    RefineBackendAppearances(),
    ...(withStartingPrediction ? [RefineBackendStartingPrediction] : []),
  ],
});

const BackendPlayerGroup = (props?: { withoutNationalTeam?: boolean }) => ({
  slug: 'player',
  title: <FormattedMessage {...cardAttributes.player} />,
  filters: [
    RefineBackendCardPlayerAge,
    ...((props?.withoutNationalTeam && []) || [RefineBackendNationality]),
    RefineBackendPlayer,
    RefineBackendActiveLeague(),
    RefineBackendActiveClub(),
    ...((props?.withoutNationalTeam && []) || [
      RefineBackendActiveNationalTeam,
    ]),
    RefineBackendPlayingStatus,
  ].filter(Boolean),
});

export const backendPrimaryMarketFilters = () => [
  {
    slug: 'PromotionalEvent',
    title: null,
    filters: [BackendPromotionalEventFilter],
  },
  BackendRarityGroup,
  BackendPriceGroup,
  ToggleGroup([BackendBidToEarnFilter()]),
  BackendWatchlistGroup,
  BackendCardGroup(),
  BackendMatchInfoGroup({ withStartingPrediction: true }),
  BackendPlayerGroup(),
  CollectibleGroup([RefineBackendSerialNumber, RefineBackendCardEdition]),
  BackendSaleGroup,
];

export const backendFlashSalePicksFilters = () => [
  BackendRarityGroup,
  BackendWatchlistGroup,
  BackendCardGroup(),
  BackendMatchInfoGroup(),
  BackendPlayerGroup(),
];

export const backendMyFootballCommonCardsFilters = ({
  filterBy,
}: {
  filterBy: (item: any) => boolean;
}) =>
  [
    {
      slug: 'skin',
      title: null,
      filters: [BackendRefineSkin()],
    },
    {
      slug: 'duplicatedKey',
      filters: [BackendDuplicateFilter],
    },
    {
      slug: 'eligibility',
      filters: [
        RefineBackendActiveLeague({
          showMore: false,
          searchable: false,
          filterBy,
        }),
      ],
    },
    {
      slug: 'position',
      title: <FormattedMessage {...FILTERS.position.title} />,
      filters: [
        RefineBackendPosition({
          hideTitle: true,
        }),
      ],
    },
    {
      slug: 'gameplay_tier',
      title: <FormattedMessage {...glossary.tier} />,
      filters: [
        RefineBackendGameplayTier({
          hideTitle: true,
        }),
      ],
    },
  ].filter(Boolean);

const BackendInSeasonFilter = {
  slug: 'inSeason',
  title: null,
  filter: BackendRefineInSeason,
  filters: [],
};

const BackendSecondaryCollectibleGroup = () =>
  CollectibleGroup([
    RefineBackendSerialNumber,
    RefineBackendCardLevel,
    RefineBackendCardEdition,
    BackendCollectibleFilter,
  ]);

export const backendSecondaryMarketFilters = () => [
  BackendInSeasonFilter,
  BackendRarityGroup,
  BackendPriceGroup,
  BackendWatchlistGroup,
  BackendCardGroup(),
  BackendMatchInfoGroup({ withStartingPrediction: true }),
  BackendPlayerGroup(),
  BackendSecondaryCollectibleGroup(),
  BackendAcceptedCurrencyGroup,
];

export const backendSecondaryMarketStackedFilters = () => [
  BackendInSeasonFilter,
  BackendPriceGroup,
  BackendCardGroup(),
  BackendSecondaryCollectibleGroup(),
  BackendAcceptedCurrencyGroup,
];

export const backendPlayerPageFilters = () => [
  BackendInSeasonFilter,
  ToggleGroup([BackendRefineOnSale(), BackendRefineCreditEligible()]),
  BackendRarityGroup,
  BackendPriceGroup,
  BackendCardGroup({ withoutCompetitionEligibility: true }),
  BackendSecondaryCollectibleGroup(),
  BackendAcceptedCurrencyGroup,
];

export const backendClubPageFilters = () => [
  BackendInSeasonFilter,
  ToggleGroup([BackendRefineOnSale(), BackendRefineCreditEligible()]),
  BackendRarityGroup,
  BackendPriceGroup,
  BackendWatchlistGroup,
  BackendCardGroup({ withoutCompetitionEligibility: true, withoutTeam: true }),
  BackendPlayerGroup(),
  BackendSecondaryCollectibleGroup(),
  BackendAcceptedCurrencyGroup,
];

export const backendLeaguePageFilters = () => [
  BackendInSeasonFilter,
  ToggleGroup([BackendRefineOnSale(), BackendRefineCreditEligible()]),
  BackendRarityGroup,
  BackendPriceGroup,
  BackendWatchlistGroup,
  BackendCardGroup({ withoutCompetitionEligibility: true }),
  BackendMatchInfoGroup(),
  BackendPlayerGroup(),
  BackendSecondaryCollectibleGroup(),
  BackendAcceptedCurrencyGroup,
];

export const backendCountryPageFilters = () => [
  BackendInSeasonFilter,
  ToggleGroup([BackendRefineOnSale(), BackendRefineCreditEligible()]),
  BackendRarityGroup,
  BackendPriceGroup,
  BackendWatchlistGroup,
  BackendCardGroup(),
  BackendMatchInfoGroup(),
  BackendPlayerGroup({ withoutNationalTeam: true }),
  BackendSecondaryCollectibleGroup(),
  BackendAcceptedCurrencyGroup,
];

export const backendTradeFilters = () => [
  BackendInSeasonFilter,
  BackendRarityGroup,
  { slug: 'not-in-lineup', filters: [BackendNotInLineupFilter] },
  BackendWatchlistGroup,
  {
    slug: 'deck',
    filters: [BackendFootballCustomDecksFilter],
  },
  BackendCardGroup(),
];

export const backendMyClubFilters = ({
  showDecksFilter = true,
}: {
  showDecksFilter?: boolean;
} = {}) =>
  [
    { slug: 'show-details', filters: [ShowDetails] },
    BackendInSeasonFilter,
    ToggleGroup([BackendRefineOnSale(), BackendNotInLineupFilter]),
    ...((showDecksFilter && [
      { slug: 'deck', filters: [BackendFootballCustomDecksFilter] },
    ]) ||
      []),
    BackendWatchlistGroup,
    BackendCardGroup(),
    BackendMatchInfoGroup(),
    BackendPlayerGroup(),
    CollectibleGroup([
      RefineBackendSerialNumber,
      RefineBackendCardLevel,
      RefineBackendCardEdition,
      BackendCollectibleFilter,
    ]),
    BackendAcceptedCurrencyGroup,
  ].filter(Boolean);

export const backendMyVaultFilters = [
  { slug: 'show-details', filters: [ShowDetails] },
  BackendInSeasonFilter,
  BackendCardGroup(),
  BackendPlayerGroup(),
  CollectibleGroup([
    RefineBackendSerialNumber,
    RefineBackendCardEdition,
    BackendCollectibleFilter,
  ]),
];

export const backendRevealableCardsFilters = [
  {
    slug: 'revealable-card-campaign',
    filters: [BackendRevealableCardCampaignMenu],
  },
  BackendCardGroup(),
  BackendPlayerGroup(),
  CollectibleGroup([RefineBackendSerialNumber, BackendCollectibleFilter]),
];
